export type RgbaU32 = number & {_brand: 'rgbau32'};

// https://v3.tailwindcss.com/docs/customizing-colors
export const AMBER_600_RGBA = 0xd97706ff as RgbaU32;
export const BLUE_600_RGBA = 0x2563ebff as RgbaU32;
export const BLUE_900_RGBA = 0x1e3a8aff as RgbaU32;
export const CYAN_400_RGBA = 0x22d3eeff as RgbaU32;
export const CYAN_600_RGBA = 0x0891b2ff as RgbaU32;
export const EMERALD_400_RGBA = 0x34d399ff as RgbaU32;
export const EMERALD_500_RGBA = 0x10b981ff as RgbaU32;
export const EMERALD_600_RGBA = 0x059669ff as RgbaU32;
export const FUCHSIA_400_RGBA = 0xe879f9ff as RgbaU32;
export const FUCHSIA_600_RGBA = 0xc026d3ff as RgbaU32;
export const GREEN_400_RGBA = 0x4ade80ff as RgbaU32;
export const GREEN_600_RGBA = 0x16a34aff as RgbaU32;
export const LIME_400_RGBA = 0xa3e635ff as RgbaU32;
export const LIME_600_RGBA = 0x65a30dff as RgbaU32;
export const NEUTRAL_200_RGBA = 0xe5e5e5ff as RgbaU32;
export const NEUTRAL_300_RGBA = 0xd4d4d4ff as RgbaU32;
export const NEUTRAL_400_RGBA = 0xa3a3a3ff as RgbaU32;
export const NEUTRAL_500_RGBA = 0x737373ff as RgbaU32;
export const NEUTRAL_600_RGBA = 0x525252ff as RgbaU32;
export const NEUTRAL_700_RGBA = 0x404040ff as RgbaU32;
export const NEUTRAL_800_RGBA = 0x262626ff as RgbaU32;
export const NEUTRAL_900_RGBA = 0x171717 as RgbaU32;
export const ORANGE_400_RGBA = 0xfb923cff as RgbaU32;
export const ORANGE_600_RGBA = 0xea580cff as RgbaU32;
export const PINK_500_RGBA = 0xec4899ff as RgbaU32;
export const PINK_600_RGBA = 0xdb2777ff as RgbaU32;
export const RED_400_RGBA = 0xf87171ff as RgbaU32;
export const RED_600_RGBA = 0xdc2626ff as RgbaU32;
export const SKY_600_RGBA = 0x0284c7ff as RgbaU32;
export const TEAL_300_RGBA = 0x5eead4ff as RgbaU32;
export const TEAL_400_RGBA = 0x2dd4bfff as RgbaU32;
export const TEAL_500_RGBA = 0x14b8a6ff as RgbaU32;
export const TEAL_950_RGBA = 0x042f2eff as RgbaU32;
export const VIOLET_400_RGBA = 0xa78bfaff as RgbaU32;
export const VIOLET_600_RGBA = 0x7c3aedff as RgbaU32;
export const VIOLET_700_RGBA = 0x6d28d9ff as RgbaU32;
export const YELLOW_600_RGBA = 0xca8a04ff as RgbaU32;

export const ACCENT_600_RGBA = VIOLET_600_RGBA as RgbaU32;
export const ACCENT_700_RGBA = VIOLET_700_RGBA as RgbaU32;
export const TRANSPARENT_RGBA = 0x00000000 as RgbaU32;

export function generateColorBlindStylePalette(): string[] {
  const colors = [
    // generated by glasbey with 12 colors, color blnd safe
    // glasbey.create_palette(palette_size=12, colorblind_safe=True)
    '#0c71ff',
    '#ca2800',
    '#ff28ba',
    '#000096',
    '#86e300',
    '#1c5951',
    '#20d2ff',
    '#20ae86',
    '#590000',
    '#65008e',
    '#b6005d',
    '#ffaa96',
    '#eeeeee',
  ];
  const grouped = [
    // Solid
    colors.map(c => `background: ${c}`),
    // Stripes
    colors.map(
      c => `background: repeating-linear-gradient(
        -45deg, ${c}, ${c} 10px, transparent 10px, transparent 20px
      )`,
    ),
    // Circles
    colors.map(
      c =>
        `background: radial-gradient(ellipse at center, ${c}, ${c} 50%, transparent 50%)`,
    ),
    // Triangles
    colors.map(
      c =>
        `background: linear-gradient(to top right, ${c}, ${c} 50%, transparent 50%)`,
    ),
    // Chevrons
    colors.map(
      c => `
      background:
        conic-gradient(from -45deg, ${c} 90deg, #0000 90.5deg),
        conic-gradient(from 135deg, ${c} 90deg, #0000 90.5deg) 12px 0,
        transparent;
      background-size: 50% 50%;
      `,
    ),
    // Overlaid circles
    colors.map(
      c => `
        background:
          radial-gradient(1em 1em at 35% 35%, ${c} 50%, transparent 50%),
          radial-gradient(1em 1em at 50% 50%, ${c} 50%, transparent 50%),
          radial-gradient(1em 1em at 65% 65%, ${c} 50%, transparent 50%);
      `,
    ),
    // More dots
    colors.map(c =>
      [
        `background-image: radial-gradient(${c} 30%, transparent 30%), ` +
          `radial-gradient(${c} 30%, transparent 30%)`,
        'background-color: transparent',
        'background-position: 0 0, 15px 15px',
        'background-size: 30px 30px',
      ].join(';'),
    ),
    // Horizontal bar
    colors.map(c =>
      [
        `background-image: linear-gradient(${c}, ${c})`,
        'background-color: transparent',
        'background-repeat: no-repeat',
        'background-position: center center',
        'background-size: 20% 100%',
      ].join(';'),
    ),
    // Suns
    colors.map(
      c =>
        `background: radial-gradient(ellipse at 100% 0%, ${c} 50%, transparent 0%)`,
    ),
    // Hashes
    colors.map(
      c =>
        'background: ' +
        `linear-gradient(to bottom right, transparent 30%, ${c} 30%, ${c} 40%, transparent 40%), ` +
        `linear-gradient(to bottom right, transparent 60%, ${c} 60%, ${c} 70%, transparent 70%), ` +
        `linear-gradient(to top right, transparent 30%, ${c} 30%, ${c} 40%, transparent 40%), ` +
        `linear-gradient(to top right, transparent 60%, ${c} 60%, ${c} 70%, transparent 70%)`,
    ),
    // Inverted circles
    colors.map(
      c =>
        `background: radial-gradient(ellipse at center, transparent, transparent 50%, ${c} 50%)`,
    ),
    // Vertical bar
    colors.map(c =>
      [
        `background-image: linear-gradient(${c}, ${c})`,
        'background-color: transparent',
        'background-repeat: no-repeat',
        'background-position: center center',
        'background-size: 100% 20%',
      ].join(';'),
    ),
  ];
  // Resort the array so that no pattern or color is contiguous
  const styles: string[] = [];
  for (let g = 0; g < grouped.length; ++g) {
    for (let c = 0; c < colors.length; ++c) {
      styles.push(grouped[(g + c) % grouped.length][c]);
    }
  }
  styles.reverse(); // I don't like the last color in the palette so pop from first-to-last
  return styles;
}
